import React from "react";
import {
  PhoneInput as BasePhoneInput,
  removeDialCode,
  defaultCountries,
  getCountry,
} from "react-international-phone";
import "react-international-phone/style.css";

// Map legacy "UK" to ISO "GB" for the library (uses ISO 3166-1 alpha-2)
const toLibraryCountry = (code) => {
  if (!code) return "us";
  const c = String(code).toUpperCase();
  return c === "UK" ? "gb" : c.toLowerCase();
};

// Map library "gb" back to "UK" if needed for API compatibility (optional)
const fromLibraryCountry = (iso2) => {
  if (!iso2) return "US";
  const c = String(iso2).toUpperCase();
  return c === "GB" ? "UK" : c;
};

// Dial codes come from the library's full country list. A hardcoded map here used
// to cover only US/CA/GB/UK/IN/AU and fall back to "1", which silently rewrote every
// other country's number as a US one (e.g. +49 151 12345678 became +1 (151) 123-4567).
const getDialCode = (countryCode) => {
  const country = getCountry({
    field: "iso2",
    value: toLibraryCountry(countryCode),
    countries: defaultCountries,
  });
  return country?.dialCode || "";
};

/**
 * Phone input with country code and flag selector.
 * Uses react-international-phone for flags (Twemoji) and full country list.
 *
 * @param {string} value - E.164 format phone (e.g. "+12025551234") or pass countryCode+phone for controlled
 * @param {string} countryCode - ISO country code (US, GB/UK, CA, IN, AU, etc.)
 * @param {string} phone - National phone digits (without country code)
 * @param {function} onChange - Called with (phone, meta) where meta has { country, inputValue }
 *   Or use onPhoneChange for { countryCode, phone, e164 }
 * @param {function} onPhoneChange - Optional: (countryCode, phone, e164) for form integration
 * @param {string} placeholder
 * @param {boolean} disabled
 * @param {boolean} required
 * @param {string|boolean} error - When truthy, shows error border styling
 * @param {string} className
 * @param {object} ...rest - Passed to base PhoneInput
 */
const PhoneInput = ({
  value,
  countryCode,
  phone,
  onChange,
  onPhoneChange,
  placeholder = "Enter your phone number",
  disabled = false,
  required = false,
  error = false,
  className = "",
  ...rest
}) => {
  // Build controlled value: prefer E.164 string, or build from countryCode + phone
  const getValue = () => {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
    if (countryCode && phone) {
      const dial = getDialCode(countryCode);
      const digits = String(phone).replace(/\D/g, "");
      if (dial && digits) return `+${dial}${digits}`;
    }
    return "";
  };

  const controlledValue = getValue();
  const defaultCountry = toLibraryCountry(countryCode || "US");

  const handleChange = (phoneValue, meta) => {
    const countryObj = meta?.country;
    const iso2 = countryObj?.iso2 ? String(countryObj.iso2).toUpperCase() : "";
    const apiCountryCode = fromLibraryCountry(iso2 || defaultCountry);
    let nationalNumber = "";
    if (phoneValue && countryObj?.dialCode) {
      nationalNumber = removeDialCode({
        phone: phoneValue,
        dialCode: countryObj.dialCode,
        prefix: "+",
        charAfterDialCode: " ",
      }).replace(/\D/g, "");
    }

    if (onChange) {
      onChange(phoneValue, meta);
    }
    if (onPhoneChange) {
      onPhoneChange(apiCountryCode, nationalNumber, phoneValue);
    }
  };

  const borderClass = error ? "border-red-400" : "border-slate-200";
  const focusBorderClass = "focus-within:border-brand-blue focus-within:ring-2 focus-within:ring-brand-blue";

  return (
    <div
      className={`react-international-phone-wrapper flex rounded-lg border bg-white ${borderClass} ${focusBorderClass} [&_.react-international-phone-input-container]:flex-1 [&_.react-international-phone-input-container]:min-w-0 [&_.react-international-phone-country-selector-dropdown]:!z-[9999] ${className}`}
    >
      <BasePhoneInput
        value={controlledValue}
        defaultCountry={defaultCountry}
        preferredCountries={["us", "ca", "gb", "in", "au"]}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        inputClassName={`flex-1 min-w-0 !border-0 !rounded-none !shadow-none bg-transparent px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:!ring-0 focus:!outline-none disabled:bg-slate-100 disabled:cursor-not-allowed`}
        countrySelectorStyleProps={{
          buttonClassName: `!rounded-none !border-0 !mr-0 !bg-white !px-3 !py-2.5 !shadow-none focus:!ring-0`,
          dropdownStyleProps: { className: "!z-[9999]" },
        }}
        {...rest}
      />
    </div>
  );
};

export default PhoneInput;
